import {
  BottomSheet,
  Checkbox,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, FormControlLabel, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useState } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { LoadingButton } from "../../components/LoadingButton";
import {
  SHIFT_DISCOVERY_LIST_PATH,
  SHIFT_DISCOVERY_WORKER_PAY_SURVEY_SUCCESS_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useCreateWorkerPaySurveyAnswer } from "../api/useCreateWorkerPaySurveyAnswer";
import { useGetWorkerPaySurveys } from "../api/useGetWorkerPaySurveys";
import { PayInputField } from "./PayInputField";

interface WorkerPaySurveyBottomSheetProps {
  modalState: UseModalState;
}

export function WorkerPaySurveyBottomSheet(props: WorkerPaySurveyBottomSheetProps) {
  const { modalState } = props;
  const { workerPaySurveyId } = useParams<{ workerPaySurveyId: string }>();
  const { userId } = useDefinedWorker();
  const history = useHistory();

  const { data: surveysData } = useGetWorkerPaySurveys();
  const survey = surveysData?.data.find((s) => s.id === workerPaySurveyId);

  const { showErrorToast } = useToast();
  const { mutateAsync: createAnswer } = useCreateWorkerPaySurveyAnswer();

  const [selectedPay, setSelectedPay] = useState<string>("");
  const [wantsNotification, setWantsNotification] = useState(false);
  const [notificationThreshold, setNotificationThreshold] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useLogEffect(
    APP_V2_USER_EVENTS.WORKER_PAY_SURVEY_PAGE_VIEWED,
    {
      workerId: userId,
      workerPaySurveyId,
    },
    { enabled: modalState.modalIsOpen && isDefined(survey) }
  );

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  const handlePayChange = useCallback((value: string) => {
    setSelectedPay(value);
  }, []);

  const handleThresholdChange = useCallback((value: string) => {
    setNotificationThreshold(value);
  }, []);

  if (!survey) {
    return null;
  }

  const { description } = survey.attributes;

  async function handleSubmit() {
    const payValue = Number.parseFloat(selectedPay);
    if (!selectedPay || Number.isNaN(payValue)) {
      showErrorToast("Please enter a valid hourly rate.");
      return;
    }

    if (isSubmitting || !survey) {
      return;
    }

    const thresholdValue = notificationThreshold
      ? Number.parseFloat(notificationThreshold)
      : undefined;
    if (
      wantsNotification &&
      (!notificationThreshold || thresholdValue === undefined || Number.isNaN(thresholdValue))
    ) {
      showErrorToast("Please enter a valid notification threshold.");
      return;
    }

    setIsSubmitting(true);
    try {
      await createAnswer({
        workerPaySurveyId: survey.id,
        estimatedPayInDollars: payValue,
        ...(wantsNotification &&
          isDefined(thresholdValue) && {
            notificationThresholdInDollars: thresholdValue,
          }),
      });

      // Navigate to success modal
      const successPath = generatePath(
        `${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}/${SHIFT_DISCOVERY_WORKER_PAY_SURVEY_SUCCESS_MODAL_PATH}`,
        { workerPaySurveyId: survey.id }
      );
      history.push(successPath);
    } catch {
      showErrorToast("Failed to submit survey. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <BottomSheet modalState={modalState} header={<BottomSheetHeader onClose={handleClose} />}>
      <DialogContent sx={{ paddingX: 7, paddingTop: 8, paddingBottom: 8 }}>
        <Stack spacing={8} alignItems="center">
          <Title variant="h2" component="h1" sx={{ textAlign: "center", textWrap: "balance" }}>
            Participate to win {formatCentsAsUsd(survey.attributes.bonusInMinorUnits)}!
          </Title>

          <Text
            variant="body1"
            sx={{ textAlign: "center", whiteSpace: "pre-line", lineHeight: 1.5 }}
          >
            {description}
          </Text>

          <PayInputField value={selectedPay} onChange={handlePayChange} />

          <FormControlLabel
            control={
              <Checkbox
                checked={wantsNotification}
                sx={{ marginRight: 2 }}
                onChange={(event) => {
                  setWantsNotification(event.target.checked);
                  if (!event.target.checked) {
                    setNotificationThreshold("");
                  }
                }}
              />
            }
            label={<Text variant="body1">Notify Me When Shifts Are Available</Text>}
            sx={{ alignSelf: "center", marginLeft: 0 }}
          />

          {wantsNotification && (
            <PayInputField
              value={notificationThreshold}
              label="Notify me when shifts are available at or above:"
              size="medium"
              fontSize="1.5rem"
              maxWidth="150px"
              onChange={handleThresholdChange}
            />
          )}

          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            disabled={!selectedPay || (wantsNotification && !notificationThreshold)}
            isLoading={isSubmitting}
            onClick={async () => {
              await handleSubmit();
            }}
          >
            Submit
          </LoadingButton>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
